// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import produce from 'immer';

import {useTranslation} from 'common/i18n/hooks';
import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {CollapsibleTheme, SectionReact} from 'common/components/section';
import {BookmarkYAxisOptions, InsightsBookmarkParams} from 'common/report/insights/util/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {ChartType} from 'common/report/constants';
import {Theme} from 'common/report/queries/app-api/themes';

import {ChartAxisCustomization, ChartCustomizationUpdate, ChartCustomizationUpdateType} from './axis/index';
import css from './index.module.styl?inline';
import styles from './index.module.styl';
import {ChartThemePicker} from './colors';

type ChartCustomizationPanelParams = {
  bookmarkParams: InsightsBookmarkParams;
  bookmarkType: BookmarkType;
  onUpdate: (
    updateType: ChartCustomizationUpdateType,
    update: Partial<ChartCustomizationUpdate>,
    diffSetting?: Partial<BookmarkYAxisOptions>,
    oldSetting?: BookmarkYAxisOptions,
  ) => void;
  onUpdateTheme: (bookmarkParams: InsightsBookmarkParams) => void;
};

const unsupportedAxisTypes = [ChartType.Bar, ChartType.StackedBar, ChartType.Pie, ChartType.Table, ChartType.Metric];

export function ChartCustomizationPanel(props: ChartCustomizationPanelParams) {
  const {t} = useTranslation();
  const bookmarkTheme = props.bookmarkParams.displayOptions.theme;
  const bookmarkThemeId = bookmarkTheme?.id;
  const bookmarkCustomTheme = (bookmarkTheme as Theme)?.data ? (bookmarkTheme as Theme) : null;

  const handleThemeSelect = (theme: Theme | null) => {
    const params = produce(props.bookmarkParams, (draftParams) => {
      draftParams.displayOptions.theme = theme?.id ? {id: theme.id} : theme;
    });
    props.onUpdateTheme(params);
  };

  const supportsChartCustomization =
    props.bookmarkType !== BookmarkType.Flows &&
    !unsupportedAxisTypes.includes(props.bookmarkParams.displayOptions.chartType);

  return (
    <div className={clsx(styles.chartCustomizationPanel)}>
      {supportsChartCustomization && (
        <SectionReact
          className={clsx(styles.section)}
          headerTitle={t(`chart-customization.axis`)}
          theme={CollapsibleTheme.INLINE}
        >
          <ChartAxisCustomization bookmarkParams={props.bookmarkParams} onUpdate={props.onUpdate} />
        </SectionReact>
      )}

      <SectionReact
        className={clsx(styles.section)}
        headerTitle={t(`chart-customization.themes`)}
        theme={CollapsibleTheme.INLINE}
      >
        <ChartThemePicker
          allowCreate
          onThemeSelect={handleThemeSelect}
          customTheme={bookmarkCustomTheme}
          activeThemeId={bookmarkThemeId}
        />
      </SectionReact>
    </div>
  );
}

export const ChartCustomizationPanelReact = withStyles(ChartCustomizationPanel, css);
export const ChartCustomizationPanelEl = react2panel(`mp-chart-customization-panel`, ChartCustomizationPanelReact, {
  contexts: [`segmentFilterController`],
});
